import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { HttpService } from 'services/http/http.service';
import { ShowIfHttpError } from '@certemy/common';
import { map } from 'rxjs/operators';
import {
  LOCATION_TYPES_OPTIONS,
  LocationCountryRow,
  LocationMultiCollapsedPanelData,
  LocationSettingResponse,
} from 'features/organization-settings/organizations-settings-locations/organizations-settings-locations.constants';
import _get from 'lodash-es/get';
import { CollapsedStateInterface } from 'components/multi-collapsed-panel/multi-collapsed-panel.constants';
import { sortItemByName } from 'utils/helpers';
import { LocationTypesResponse } from 'services/locations/locations.constant';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';

@Injectable()
export class OrganizationSettingsLocationsService {
  _collapsedState: CollapsedStateInterface;

  constructor(private http: HttpService) {}

  @ShowIfHttpError('An error occurred while getting location settings.')
  getLocationSettings(): Observable<LocationCountryRow[]> {
    return this.http.get('/location').pipe(map((response: LocationSettingResponse) => response.rows));
  }

  @ShowIfHttpError('An error occurred while getting location data.')
  getMappedLocationSettings(): Observable<LocationTypesResponse> {
    return this.http.get('/location/dictionary');
  }

  locationResponseToDataMapper(
    rows: LocationCountryRow[] | LocationMultiCollapsedPanelData[],
  ): LocationMultiCollapsedPanelData {
    return {
      root: true,
      children: this.addRowsOptions(rows, _get(this._collapsedState, 'children')),
    };
  }

  private addRowsOptions(rows, collapsedState) {
    return sortItemByName(
      rows.reduce((arr, row) => {
        let displayName = '';
        if (row.address1) {
          // office
          const { name, city, address1, address2, stateCode, postalCode } = row;
          const address = `${address1}` + (address2 ? ' ' + `${address2}` : '');
          const state = stateCode ? ` ${stateCode}` : '';
          displayName += `${name} | ${address} ${city},${state} ${postalCode}`;
        }
        return [
          ...arr,
          {
            ...row,
            displayName,
            expanded: _get(collapsedState, `${row.id}.value`, false),
            options: LOCATION_TYPES_OPTIONS[row.locationTypeId],
            ...(row.children
              ? { children: this.addRowsOptions(row.children, _get(collapsedState, `${row.id}.children`)) }
              : {}),
          },
        ];
      }, []),
    );
  }

  setCollapsedState(data: CollapsedStateInterface) {
    this._collapsedState = data;
  }
}

export const organizationSettingsLocationsResolver: ResolveFn<Observable<LocationCountryRow[]>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(OrganizationSettingsLocationsService).getLocationSettings();
};
